/**
 * MonthGrid — renders a full month calendar grid with ARIA roles,
 * day-of-week headers, DayCell components, and DateSpan overlays.
 * Supports arrow key navigation between cells with wrapping at grid edges.
 *
 * Validates: Requirements 8.1, 8.4, 8.5, 10.1, 10.2
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DayCell } from './DayCell';
import { DateSpan } from './DateSpan';
import { getMonthGrid } from '../../utils/date-utils';
import type { DateSpanData } from '../../utils/date-utils';
import type { CardSummary, ListSummary } from '../../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MonthGridProps {
  readonly year: number;
  readonly month: number; // 0-indexed
  readonly cardsByDate: ReadonlyMap<string, readonly CardSummary[]>;
  readonly dateSpans: readonly DateSpanData[];
  readonly lists: readonly ListSummary[];
  readonly onDrop: (cardId: string, targetDate: string) => void;
  readonly onCardClick: (cardId: string) => void;
  /** Keyboard reschedule: move card left/right by one day */
  readonly onRescheduleMove?: (cardId: string, direction: 'left' | 'right') => void;
  /** Keyboard reschedule: confirm the move */
  readonly onRescheduleConfirm?: (cardId: string) => void;
  /** Keyboard reschedule: cancel the move */
  readonly onRescheduleCancel?: (cardId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTH_MAX_VISIBLE = 2;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MonthGrid({
  year,
  month,
  cardsByDate,
  dateSpans,
  lists,
  onDrop,
  onCardClick,
  onRescheduleMove,
  onRescheduleConfirm,
  onRescheduleCancel,
}: MonthGridProps): React.ReactElement {
  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);

  // Flatten all cells for linear index-based navigation
  const allCells = useMemo(() => grid.weeks.flat(), [grid.weeks]);
  const totalCells = allCells.length;

  // Track which cell is focused (by flat index)
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  // Track drop target date for highlighting
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);

  // Refs for each cell to manage programmatic focus
  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Handle arrow key navigation on the grid
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let nextIndex: number | null = null;

      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (focusedIndex + 1) % totalCells;
          break;
        case 'ArrowLeft':
          nextIndex = (focusedIndex - 1 + totalCells) % totalCells;
          break;
        case 'ArrowDown':
          nextIndex = (focusedIndex + 7) % totalCells;
          break;
        case 'ArrowUp':
          nextIndex = (focusedIndex - 7 + totalCells) % totalCells;
          break;
        default:
          return;
      }

      e.preventDefault();
      setFocusedIndex(nextIndex);
      cellRefs.current[nextIndex]?.focus();
    },
    [focusedIndex, totalCells],
  );

  // Handle cell focus via click
  const handleCellFocus = useCallback((flatIndex: number) => {
    setFocusedIndex(flatIndex);
  }, []);

  // Handle drop with target date
  const handleDrop = useCallback(
    (cardId: string, targetDate: string) => {
      setDropTargetDate(null);
      onDrop(cardId, targetDate);
    },
    [onDrop],
  );

  return (
    <div
      role="grid"
      aria-label={`Calendar grid for ${new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
      className="w-full"
      onKeyDown={handleKeyDown}
    >
      {/* Day-of-week header row */}
      <div role="row" className="grid grid-cols-7">
        {DAY_HEADERS.map((day) => (
          <div
            key={day}
            role="columnheader"
            className="text-center text-xs font-semibold text-muted-foreground py-2 border-b border-border"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Week rows with cells */}
      {grid.weeks.map((week, weekIndex) => (
        <div key={weekIndex} className="relative">
          {/* Day cells row */}
          <div role="row" className="grid grid-cols-7">
            {week.map((cell, dayIndex) => {
              const flatIndex = weekIndex * 7 + dayIndex;
              const cards = cardsByDate.get(cell.date) ?? [];
              const isFocused = flatIndex === focusedIndex;

              return (
                <div
                  key={cell.date}
                  ref={(el) => { cellRefs.current[flatIndex] = el; }}
                  tabIndex={isFocused ? 0 : -1}
                  onFocus={() => handleCellFocus(flatIndex)}
                  className="outline-none"
                >
                  <DayCell
                    date={cell.date}
                    isCurrentMonth={cell.isCurrentMonth}
                    isToday={cell.isToday}
                    cards={cards}
                    maxVisible={MONTH_MAX_VISIBLE}
                    lists={lists}
                    isDropTarget={dropTargetDate === cell.date}
                    onDrop={(cardId) => handleDrop(cardId, cell.date)}
                    onCardClick={onCardClick}
                    onRescheduleMove={onRescheduleMove}
                    onRescheduleConfirm={onRescheduleConfirm}
                    onRescheduleCancel={onRescheduleCancel}
                  />
                </div>
              );
            })}
          </div>

          {/* DateSpan overlay for this week row */}
          <div
            className="absolute inset-0 grid grid-cols-7 pointer-events-none"
            aria-hidden="true"
          >
            <div className="col-span-7 flex flex-col gap-0.5 pt-6">
              <div className="grid grid-cols-7">
                {dateSpans.map((span) => (
                  <DateSpan
                    key={`${span.card.cardId}-week-${weekIndex}`}
                    span={span}
                    visibleStart={week[0]?.date ?? ''}
                    visibleEnd={week[6]?.date ?? ''}
                    onClick={() => onCardClick(span.card.cardId)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
